import * as React from "react";
import { EdgeProps, getSmoothStepPath, Position } from "reactflow";

export interface IOrthogonalPoint {
  x: number;
  y: number;
}

// How far a baked endpoint may sit from where react-flow actually anchors the
// edge before the route is treated as stale. A couple of pixels covers rounding;
// anything more means the node moved (a drag) and ELK's channels no longer apply.
// The handles sit 2px outside the card edge (`.column-handle` offsets) while the
// ELK ports sit exactly on it, so a correct route is already ~2px out. 6px keeps
// that inside tolerance and still catches a drag.
const STALE_TOLERANCE = 6;

const CORNER_RADIUS = 8;

// Staleness is judged on Y alone. The row a wire leaves from is what ELK's
// channels are built around, so a moved row invalidates the route; X can differ
// by a couple of pixels because the handles sit just outside the card edge, and
// the endpoint is taken from react-flow anyway (see buildRoute).
function rowMoved(a: IOrthogonalPoint, y: number): boolean {
  return Math.abs(a.y - y) > STALE_TOLERANCE;
}

/**
 * Draws a polyline through `points` with rounded corners. Each corner is cut back
 * by up to CORNER_RADIUS along both adjoining segments, so a short segment shrinks
 * its own radius instead of overshooting into the next one.
 */
function buildRoundedPath(points: readonly IOrthogonalPoint[]): string {
  if (points.length < 2) return "";

  let d = `M ${points[0].x},${points[0].y}`;

  for (let i = 1; i < points.length - 1; i++) {
    const previous: IOrthogonalPoint = points[i - 1];
    const corner: IOrthogonalPoint = points[i];
    const next: IOrthogonalPoint = points[i + 1];

    const inLength: number = Math.hypot(corner.x - previous.x, corner.y - previous.y);
    const outLength: number = Math.hypot(next.x - corner.x, next.y - corner.y);
    const radius: number = Math.min(CORNER_RADIUS, inLength / 2, outLength / 2);

    if (radius < 0.5) {
      d += ` L ${corner.x},${corner.y}`;
      continue;
    }

    const inRatio: number = radius / inLength;
    const outRatio: number = radius / outLength;
    const entryX: number = corner.x - (corner.x - previous.x) * inRatio;
    const entryY: number = corner.y - (corner.y - previous.y) * inRatio;
    const exitX: number = corner.x + (next.x - corner.x) * outRatio;
    const exitY: number = corner.y + (next.y - corner.y) * outRatio;

    d += ` L ${entryX},${entryY} Q ${corner.x},${corner.y} ${exitX},${exitY}`;
  }

  const last: IOrthogonalPoint = points[points.length - 1];
  return `${d} L ${last.x},${last.y}`;
}

/**
 * Edge that prefers the orthogonal route ELK computed for the whole graph — the
 * only stage that can assign shared channels and minimise crossings — and falls
 * back to react-flow's local step router the moment those points stop matching
 * the endpoints, which is what happens while a node is being dragged.
 */
export const OrthogonalEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  style,
  data
}: EdgeProps): JSX.Element => {
  const points: IOrthogonalPoint[] | undefined = data?.points;

  const path: string = React.useMemo((): string => {
    const usable: boolean =
      !!points && points.length >= 2 && !rowMoved(points[0], sourceY) && !rowMoved(points[points.length - 1], targetY);

    if (usable && points) {
      // ELK's first and last segments leave the ports horizontally, so replacing
      // just their X with the real handle position keeps the route orthogonal and
      // removes any dependence on the node width ELK was given.
      const route: IOrthogonalPoint[] = points.map((point: IOrthogonalPoint): IOrthogonalPoint => ({
        x: point.x,
        y: point.y
      }));
      route[0] = { x: sourceX, y: route[0].y };
      route[route.length - 1] = { x: targetX, y: route[route.length - 1].y };
      return buildRoundedPath(route);
    }

    const [fallback] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition: Position.Right,
      targetX,
      targetY,
      targetPosition: Position.Left,
      borderRadius: CORNER_RADIUS
    });
    return fallback;
  }, [points, sourceX, sourceY, targetX, targetY]);

  return <path id={id} className="react-flow__edge-path" d={path} style={style} markerEnd={markerEnd} fill="none" />;
};
